/**
 * Servizio batch per la risoluzione del titolo di destinazione di un link.
 * Raccoglie tutti gli href registrati in un frame di rendering e li invia
 * in un'unica chiamata API, riducendo il numero di richieste.
 *
 *  - Se ariaLabel esplicito esiste → mantenerlo invariato
 *  - Se manca ariaLabel e c'è label + titolo destinazione → "{label} - {titoloDestinazione}"
 *  - Se manca titolo destinazione o href → solo label
 *  - Se non esiste nemmeno label → nessun fallback artificiale
 */

import { API_FULL_PATH, API_PREFIX } from 'utils'

// ─── State module-level (singleton browser) ─────────────────────────────────

/** path → title | null (null = risolto ma non trovato) */
const cache = new Map<string, string | null>()

/** paths accodati ma non ancora fetchati */
const pending = new Set<string>()

/** listener da chiamare dopo ogni flush */
const subscribers = new Set<() => void>()

let flushTimer: ReturnType<typeof setTimeout> | null = null

const LOCALE_PREFIX_RE = /^\/(it-it|int|es-es|en-gb|it|en|es|default)(?=\/|$)/i

/**
 * Normalizza un href in un path coerente con la chiave restituita dal backend.
 */
export function normalizeLinkPath(pathOrUrl: string): string {
  const raw = String(pathOrUrl || '').trim()
  if (!raw) return '/'

  let path = raw

  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost'
    path = new URL(raw, base).pathname
  } catch {
    // fallback su parsing stringa manuale
    const [beforeQuery = ''] = raw.split('?')
    const [beforeHash = ''] = beforeQuery.split('#')
    path = beforeHash
  }

  const [pathBeforeQuery = '/'] = (path || '/').split('?')
  const [pathBeforeHash = '/'] = pathBeforeQuery.split('#')
  path = pathBeforeHash
  path = path.startsWith('/') ? path : `/${path}`
  path = path.replace(/\/+$/, '')
  if (!path) return '/'

  path = path.replace(LOCALE_PREFIX_RE, '') || '/'
  return path
}

function notify() {
  subscribers.forEach((cb) => cb())
}

// ─── Core ─────────────────────────────────────────────────────────────────

async function flush() {
  flushTimer = null
  const normalizedPending = [...pending].map((pendingPath) => normalizeLinkPath(pendingPath))
  const toFetch = [...new Set(normalizedPending)].filter((p) => !cache.has(p))
  pending.clear()

  if (toFetch.length === 0) {
    notify()
    return
  }

  try {
    const qs = toFetch.map((p) => `path=${encodeURIComponent(p)}`).join('&')
    const apiBase = API_FULL_PATH || API_PREFIX
    const separator = apiBase.endsWith('/') ? '' : '/'
    const res = await fetch(`${apiBase}${separator}link-titles/?${qs}`)
    if (!res.ok) throw new Error(`link-titles API error ${res.status}`)
    const json: { items: { pathNormalized: string; title: string | null }[] } = await res.json()
    json.items.forEach((item) => {
      cache.set(item.pathNormalized, item.title)
    })
  } catch {
    // in caso di errore segniamo null per tutti i path non ancora risolti
    toFetch.forEach((p) => {
      if (!cache.has(p)) cache.set(p, null)
    })
  }

  notify()
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Aggiunge un path alla coda di risoluzione.
 * Il fetch avviene in modo differito (prossimo frame) per raccogliere
 * tutti i path di un ciclo di rendering in un'unica chiamata.
 */
export function scheduleLinkTitle(path: string): void {
  const normalizedPath = normalizeLinkPath(path)
  if (cache.has(normalizedPath)) return
  pending.add(normalizedPath)
  if (!flushTimer) {
    flushTimer = setTimeout(flush, 16)
  }
}

/**
 * Legge il titolo risolto dalla cache.
 * - `undefined` = non ancora risolto
 * - `null`      = URL non risolvibile
 * - `string`    = titolo trovato
 */
export function getCachedTitle(path: string): string | null | undefined {
  const normalizedPath = normalizeLinkPath(path)
  return cache.has(normalizedPath) ? cache.get(normalizedPath)! : undefined
}

/**
 * Iscrive un callback alle notifiche di aggiornamento della cache.
 * Restituisce la funzione di disiscrizione.
 */
export function subscribeLinkTitles(cb: () => void): () => void {
  subscribers.add(cb)
  return () => subscribers.delete(cb)
}

// ─── Aria-label builder (pura, testabile) ────────────────────────────────────

/**
 * Compone l'aria-label secondo le regole del piano:
 *  - ariaLabel esplicito → invariato
 *  - label + title → "label - title"
 *  - solo label → "label"
 *  - nessuno dei due → undefined
 */
export function buildAriaLabel(
  ariaLabel: string | undefined,
  label: string | undefined,
  title: string | null | undefined
): string | undefined {
  const normalizedAriaLabel = ariaLabel?.trim()
  const normalizedLabel = label?.trim()
  const ariaIsExplicit =
    !!normalizedAriaLabel && (!normalizedLabel || normalizedAriaLabel !== normalizedLabel)

  if (ariaIsExplicit) return normalizedAriaLabel

  const baseLabel = normalizedLabel || normalizedAriaLabel
  if (!baseLabel) return undefined
  if (title) return `${baseLabel} - ${title}`
  return baseLabel
}
